import {
  DEFAULT_VISIBLE_OPTIONAL_COLUMNS,
  type GanttOptionalGridColumnKey,
  type GanttVisibleOptionalColumns,
} from '../features/schedule-tasks/components/GanttGridColumnToggles';

const STORAGE_PREFIX = 'falcon-planner:gantt-visible-columns:';

export type GanttVisibleOptionalColumnsSession = {
  version: 1;
  columns: GanttVisibleOptionalColumns;
};

const KNOWN_KEYS = Object.keys(
  DEFAULT_VISIBLE_OPTIONAL_COLUMNS,
) as GanttOptionalGridColumnKey[];

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

function normalizeColumns(
  partial: Partial<Record<string, unknown>> | null | undefined,
): GanttVisibleOptionalColumns {
  const next: GanttVisibleOptionalColumns = { ...DEFAULT_VISIBLE_OPTIONAL_COLUMNS };
  if (!partial || typeof partial !== 'object') return next;
  for (const key of KNOWN_KEYS) {
    if (typeof partial[key] === 'boolean') {
      next[key] = partial[key];
    }
  }
  return next;
}

/** Load optional grid column visibility for this tab session (per project). */
export function loadGanttVisibleOptionalColumns(
  projectId: string,
): GanttVisibleOptionalColumns {
  if (!projectId || typeof window === 'undefined') {
    return { ...DEFAULT_VISIBLE_OPTIONAL_COLUMNS };
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey(projectId));
    if (!raw) return { ...DEFAULT_VISIBLE_OPTIONAL_COLUMNS };
    const parsed = JSON.parse(raw) as GanttVisibleOptionalColumnsSession;
    if (!parsed || parsed.version !== 1 || !parsed.columns || typeof parsed.columns !== 'object') {
      return { ...DEFAULT_VISIBLE_OPTIONAL_COLUMNS };
    }
    return normalizeColumns(parsed.columns);
  } catch {
    return { ...DEFAULT_VISIBLE_OPTIONAL_COLUMNS };
  }
}

/** Persist optional grid column visibility for the rest of this tab session. */
export function saveGanttVisibleOptionalColumns(
  projectId: string,
  columns: GanttVisibleOptionalColumns,
): void {
  if (!projectId || typeof window === 'undefined') return;
  const next: GanttVisibleOptionalColumnsSession = {
    version: 1,
    columns: normalizeColumns(columns),
  };
  try {
    window.sessionStorage.setItem(storageKey(projectId), JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}
